import * as React from 'react';
import {useEffect, useState, useMemo} from 'react';
import {SearchService} from 'features/search/services/SearchService';
import {SearchTypes} from 'features/search/enums';
import {SearchResponse, SearchResult} from 'features/search/types';
import {useDeviceStore, selectIsMobile} from 'features/ui/store/useDeviceStore';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {useSearchStore, selectSearchFilters, selectCurrentResidenceType} from 'features/search/store/useSearchStore';
import {convertFiltersForApi} from 'features/search/helpers';
import {ObjectStatus, ResidenceType, SortBy} from 'features/search/filters/enums';

import './_cardHolder.scss';

import {Icon} from 'components/atoms/Media/Icon';
import {GeoJson} from '@organisms/Map/index';
import {CardSwitcher} from 'components/molecules/SlidingContent/CardSwitcher';
import {Button, ButtonColor, ButtonSize, ButtonType} from '@atoms/LinksAndButtons/Button';
import {FormattedMessage} from 'react-intl';
import { ResidenceCardType, ResidenceCard } from '@molecules/ResidentialCards/ResidenceCard';

interface Props {
    jsonData: GeoJson;
    onCloseClick: () => void;
}

export const CardHolder: React.FunctionComponent<Props> = ({jsonData, onCloseClick}) => {
    const [objects, setObjects] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const currentResidenceType = useSearchStore(selectCurrentResidenceType);
    const isMobile = useDeviceStore(selectIsMobile);
    const searchFilters = useSearchStore(selectSearchFilters);
    const filters = useMemo(() => convertFiltersForApi(searchFilters), [searchFilters]);


    useEffect(() => {
        setLoading(true);
        getObjectsForStage();
    }, [filters, jsonData]);

    const getObjectsForStage = async () => {
        const response = await SearchService.fetchObjects({
            ...filters,
            stageId: [jsonData.properties.stageId],
            objectStatus: [ObjectStatus.FOR_SALE],
        }, SortBy.ACCESS_ASC, 0, 150);


        setObjects(response.data?.hits ?? []);
        setLoading(false);
    };

    const isTypeHouse = (category: string) => {
        return category === ResidenceType.HOUSE
            || category === ResidenceType.TOWN_HOUSE
            || category === ResidenceType.SEMI_DETACHED_HOUSE
            || category === ResidenceType.DETACHED_HOUSE;
    };

    const getContentAffix = (category: string) => {
        if(isTypeHouse(category)) {
            return getFormattedMessage('jm.components.SearchResultMapView.houses')
        }

        if(category === ResidenceType.APARTMENT) {
            return getFormattedMessage('jm.components.SearchResultMapView.apartments')
        }

        if(category === ResidenceType.PLOT) {
            return getFormattedMessage('jm.components.SearchResultMapView.plots')
        }

        return '';
    };

    const _hasShowing = () => {
        if(!jsonData.properties.residence) return false;

        if(jsonData.properties.residence.hasExtraShowingsInfo) return true;
        if(jsonData.properties.residence.showings.length > 0) return true;

        return false;
    };

    return (
        <div className="card-holder">
            <Button
                icon={<Icon name="x" />}
                className="card-holder__close"
                onClick={onCloseClick}
                iconBtn 
                buttonColor={ButtonColor.LIGHT} 
                buttonSize={ButtonSize.DEFAULT} 
                buttonType={ButtonType.DEFAULT} 
            >
                <FormattedMessage id="jm.components.Overlay.close" />
            </Button>

            {(currentResidenceType === SearchTypes.STAGE || currentResidenceType === SearchTypes.AREA || currentResidenceType === SearchTypes.OBJECTS && objects.length === 0 && !loading) &&
                <ResidenceCard
                    residence={jsonData.properties.residence} 
                    cardType={ResidenceCardType.PROJECT} 
                    horizontal={!isMobile} 
                />
            }

            {(currentResidenceType === SearchTypes.OBJECTS && objects.length > 0 && !loading) &&
                <div>
                    <CardSwitcher
                        searchResults={objects}
                        counterAffix={getContentAffix(jsonData.properties.category)}
                    >
                        {objects.map((object) => (
                            <ResidenceCard
                                residence={object} 
                                key={object.id}  
                                cardType={ResidenceCardType.OBJECT} 
                                horizontal={!isMobile}
                            />
                        ))}
                    </CardSwitcher>
                </div>
            }
        </div>
    );
};
